import { useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { type AuthState, AUTH_STATE_STORAGE_KEY } from '@/components/auth/auth-google'
import { useUserContext } from '@/context/userLogin'
import { type ErrorCode } from '@react-oauth/google'
import { trpc } from '@/config/trpc'
import { toast } from 'sonner'
import {
  type ConnectFailReason,
  providerErrorReason,
  trackConnectorConnectFailed,
  trackPlatformConnectFailed,
} from '@/lib/analytics/connectionTracking'

// Login has its own auth events; only the YouTube and Drive connect flows are tracked here.
const trackFailed = (flow: AuthState['flow'], reason: ConnectFailReason, error?: unknown) => {
  if (flow === 'youtube') trackPlatformConnectFailed('youtube', reason, error)
  if (flow === 'drive') trackConnectorConnectFailed('google_drive', reason, error)
}

// Where a flow lands when its stored state is unusable and there is no referrer to return to.
const FALLBACK_PATH: Record<AuthState['flow'], string> = {
  login: '/login',
  youtube: '/settings/social-media',
  drive: '/',
}

// The state comes back through the URL, so it can be missing or garbage. Without it we cannot
// tell login from a connect flow.
const parseReturnedState = (state: string | null): AuthState | null => {
  if (!state) return null
  try {
    const parsed: unknown = JSON.parse(state)
    return parsed && typeof parsed === 'object' && 'rnd' in parsed && 'flow' in parsed
      ? (parsed as AuthState)
      : null
  } catch {
    return null
  }
}

export function AuthGooglePage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const { onCodeResponse } = useUserContext()
  const { mutateAsync: authorizeService } = trpc.service.authorize.useMutation()
  const { mutateAsync: authorizePlatform } = trpc.platform.authorize.useMutation()

  const isMutationRunning = useRef(false)

  // Get params from URL
  const state = searchParams.get('state')
  const code = searchParams.get('code')
  const scope = searchParams.get('scope')
  const error = searchParams.get('error')
  const error_description = searchParams.get('error_description')
  const error_uri = searchParams.get('error_uri')

  useEffect(() => {
    if (isMutationRunning.current) {
      console.debug('Mutation already in progress, skipping duplicate call')
      console.debug('This should only happein in development due to StrictMode')
      return
    }

    isMutationRunning.current = true

    const returnedState = parseReturnedState(state)
    if (!state || !returnedState) {
      console.warn(`Unusable state returned from Google: ${state}`)
      // Could be a login, so no connect-failed event. Just get the user off the spinner.
      isMutationRunning.current = false
      localStorage.removeItem(AUTH_STATE_STORAGE_KEY)
      toast.error('Something went wrong. Please try again.')
      navigate('/')
      return
    }

    const storedState =
      // NOTE: special case when redirecting from our landing page
      returnedState.rnd === 'google'
        ? returnedState
        : (JSON.parse(localStorage.getItem(AUTH_STATE_STORAGE_KEY) || 'null') as AuthState | null)

    if (!storedState || storedState.rnd !== returnedState.rnd) {
      console.warn(`States did not match:\n\tstored: ${storedState}\n\trecievedState: ${state}`)
      // Same recovery as the Facebook page: without it the spinner spins forever. The flow
      // comes from the returned state since the stored one is missing or someone else's.
      isMutationRunning.current = false
      localStorage.removeItem(AUTH_STATE_STORAGE_KEY)
      trackFailed(returnedState.flow, 'state_mismatch')
      toast.error('Google authentication state mismatch. Please try again.')
      navigate(FALLBACK_PATH[returnedState.flow] ?? '/')
      return
    }

    const getServiceName = () => {
      if (storedState.flow === 'drive') return 'Google Drive'
      if (storedState.flow === 'youtube') return 'YouTube'
      return 'Google'
    }

    const onSuccess = () => {
      if (storedState.flow !== 'login') {
        toast.success(`${getServiceName()} connected successfully`)
      }
      navigate(storedState.referrerPath)
    }
    const onError = (errorMessage?: string) => {
      toast.error(`Failed to connect ${getServiceName()}`, {
        description: errorMessage,
      })
      navigate(storedState.referrerPath)
    }

    new Promise<void>((resolve, reject) => {
      if (code && scope) {
        const shouldAuthorize = () => {
          if (storedState.flow === 'login') {
            return onCodeResponse(
              {
                code,
                scope,
                error: error as ErrorCode,
                error_description: error_description || undefined,
                error_uri: error_uri || undefined,
                state,
              },
              storedState.redirectUri,
            )
          }

          if (storedState.flow === 'drive') {
            return authorizeService({
              service: 'google_drive',
              code,
              redirectUri: storedState.redirectUri,
            })
          }

          if (storedState.flow === 'youtube') {
            return authorizePlatform({
              platform: 'youtube',
              code,
              redirectUri: storedState.redirectUri,
            })
          }

          throw new Error(`Invalid flow: ${storedState.flow}`)
        }

        shouldAuthorize()
          .then(() => resolve())
          .catch((e) => {
            trackFailed(storedState.flow, 'authorize_failed', e)
            reject(e)
          })
      } else if (error) {
        const reason = providerErrorReason(error)
        trackFailed(storedState.flow, reason, error)
        reject({
          message: 'Error authenticating with Google',
          error,
          error_description,
          error_uri,
          cancelled: reason === 'cancelled',
        })
      } else {
        trackFailed(storedState.flow, 'missing_code')
        reject('BUG: Code and Error not provided')
      }
    })
      .then(() => onSuccess())
      .catch((error) => {
        // Backing out of the consent screen is a choice, not a failure worth a red toast.
        if (error?.cancelled) {
          toast(
            storedState.flow === 'login'
              ? 'Google sign-in cancelled'
              : `${getServiceName()} connection cancelled`,
          )
          navigate(storedState.referrerPath)
          return
        }
        console.error(error)
        onError(error?.message || error?.error_description)
      })
      .finally(() => {
        localStorage.removeItem(AUTH_STATE_STORAGE_KEY)
        isMutationRunning.current = false
      })
  }, [
    state,
    code,
    scope,
    error,
    error_description,
    error_uri,
    onCodeResponse,
    authorizeService,
    authorizePlatform,
    navigate,
  ])

  return (
    <div className="flex h-screen items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="border-primary/30 border-t-primary h-12 w-12 animate-spin rounded-full border-4"></div>
        <p className="text-muted-foreground">Connecting Google...</p>
      </div>
    </div>
  )
}
